import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["createNewFooter", "defaultOptions", "error", "extraDropdownFooterContent", "menuStyles", "options", "placeholder", "readOnly", "type", "useOnDark", "valueRenderer", "fixedHeight"];
import { PureComponent } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { isEqual } from 'underscore';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Select } from 'foundations-components/transitional/components/Select';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Fragment } from 'react';
import { isNullOrUndefined, isObject } from 'ContentUtils/helpers/ObjectHelpers';
import { FAILED } from 'ContentUtils/constants/RequestStatus';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = suffix => `ContentComponents.cmv2.fields.baseResource.${suffix}`;
const isEmptyValue = value => isNullOrUndefined(value) || value === '' || Array.isArray(value) && !value.length;
class AsyncSelector extends PureComponent {
  constructor(props) {
    super(props);
    this.loadOptions = this.loadOptions.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onOpenChange = this.onOpenChange.bind(this);
    this.state = {
      isOpen: false,
      isUpdatingDefaultOptions: false
    };
  }
  componentDidMount() {
    const {
      defaultOptions,
      value
    } = this.props;
    const defaultOptionsKey = Array.isArray(value) ? JSON.stringify(value) : `${value}`;
    const defaultOptionsValueMap = defaultOptions.reduce((acc, option) => {
      const {
        value: defaultOptionValue
      } = option;
      acc[`${defaultOptionValue}`] = defaultOptionValue;
      return acc;
    }, {});
    this.defaultOptionsData = isEmptyValue(value) ? Object.assign({}, defaultOptionsValueMap) : Object.assign({}, defaultOptionsValueMap, {
      [defaultOptionsKey]: value
    });
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      clearCache,
      defaultOptions
    } = this.props;
    const {
      isOpen,
      isUpdatingDefaultOptions
    } = this.state;
    if (!isEqual(prevProps.defaultOptions, defaultOptions)) {
      defaultOptions.reduce((updatedDefaultOptionsData, option) => {
        if ('value' in option && !isEmptyValue(option.value)) {
          const defaultOptionsKey = Array.isArray(option.value) ? JSON.stringify(option.value) : `${option.value}`;
          if (updatedDefaultOptionsData[defaultOptionsKey] === undefined) {
            updatedDefaultOptionsData[defaultOptionsKey] = option.value;
          }
        }
        return updatedDefaultOptionsData;
      }, this.defaultOptionsData);
      this.setState({
        isUpdatingDefaultOptions: false
      });
      clearCache();
    }
    if (!isUpdatingDefaultOptions && prevState.isOpen !== isOpen) {
      clearCache();
    }
  }
  onOpenChange(evt) {
    const {
      onOpenChange
    } = this.props;
    if (onOpenChange) {
      onOpenChange(evt);
    }
    const isOpen = evt.target.value;
    if (!isOpen) {
      this.resetOffset = true;
    }
    this.setState({
      isOpen
    });
  }
  onChange(evt) {
    const {
      clearCache,
      onChange,
      saveCache
    } = this.props;
    const newValue = evt.target.value;
    const defaultOptionsKey = Array.isArray(newValue) ? JSON.stringify(newValue) : `${newValue}`;
    if (!isEmptyValue(newValue) && this.defaultOptionsData[defaultOptionsKey] === undefined) {
      this.setState({
        isUpdatingDefaultOptions: true
      });
      saveCache(evt.target.value);
    } else {
      clearCache();
    }
    onChange(evt);
  }

  /* Proxy load options to reset offset when closed */
  loadOptions(input, callback, pagination = {
    offset: 0
  }) {
    let __pagination = pagination;
    if (this.resetOffset) {
      __pagination = {
        offset: 0
      };
      this.resetOffset = false;
    }
    this.props.loadOptions(input, callback, __pagination);
  }
  renderRequestFailedAlert() {
    const {
      optionsRequestStatus
    } = this.props;
    return optionsRequestStatus === FAILED && /*#__PURE__*/_jsx(Alert, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`failedOptionsRequest.title`)
      }),
      type: "danger",
      marginBlockEnd: "300",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`failedOptionsRequest.desc`)
      })
    });
  }
  render() {
    const _this$props = this.props,
      {
        createNewFooter,
        defaultOptions,
        error,
        extraDropdownFooterContent,
        menuStyles,
        options,
        placeholder: userPlaceholder,
        readOnly,
        type,
        useOnDark,
        valueRenderer,
        fixedHeight
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      isOpen,
      isUpdatingDefaultOptions
    } = this.state;
    let {
      value
    } = this.props;
    if (isObject(value)) {
      value = undefined;
    }
    const __loadOptions = isOpen ? this.loadOptions : undefined;
    const __options = isOpen || isUpdatingDefaultOptions ? options : defaultOptions;
    const placeHolder = userPlaceholder || (type ? unescapedText(i18nKey('placeholder'), {
      type: unescapedText(i18nKey(`typeLabels.${type}`), {
        count: 1
      })
    }) : undefined);
    const dropdownFooter = createNewFooter || extraDropdownFooterContent ? /*#__PURE__*/_jsxs(TFlex, {
      direction: "column",
      children: [extraDropdownFooterContent, createNewFooter]
    }) : null;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [this.renderRequestFailedAlert(), /*#__PURE__*/_jsx(Select, Object.assign({
        buttonUse: useOnDark ? 'form-on-dark' : 'form',
        "data-selenium": this.props['data-selenium'],
        "data-test-id": "async-selector",
        "data-value": value,
        dropdownFooter: dropdownFooter,
        error: error,
        menuStyles: menuStyles,
        options: __options,
        placeholder: placeHolder,
        readOnly: readOnly,
        valueRenderer: valueRenderer
      }, rest, {
        loadOptions: __loadOptions,
        onChange: this.onChange,
        onOpenChange: this.onOpenChange,
        value: value,
        blockSize: fixedHeight
      }))]
    });
  }
}
AsyncSelector.defaultProps = {
  cache: null,
  clearCache: emptyFunction,
  defaultOptions: [],
  filterOptions: false,
  options: [],
  saveCache: emptyFunction,
  value: ''
};
export default AsyncSelector;